// contexts/CompetitionContext.jsx
import React, { createContext, useContext, useState, useCallback } from 'react';
import {
    getCompetitionByOrganiser,
    getCompetitionById,
    CreateCompetition as createCompetitionAPI
} from '../services/apiServices';

export const useCompetition = () => {
    const context = useContext(CompetitionContext);
    if (!context) {
        throw new Error('useCompetition must be used within CompetitionProvider');
    }
    return context;
};

const CompetitionContext = createContext();

export const CompetitionProvider = ({ children }) => {
    const [competitions, setCompetitions] = useState([]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);



    // Load competitions from API
    const loadCompetitions = useCallback(async (organiserId) => {
        setLoading(true);
        setError(null);
        try {
            const response = await getCompetitionByOrganiser(organiserId);
            if (response === "false") {
                throw new Error('Failed to fetch competitions');
            }

            const data = await response.json();
            setCompetitions(data || []);
            return data || [];
        } catch (err) {
            setError(err.message);
            console.error('Error loading competitions:', err);
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    // Load single competition by ID
    const loadCompetitionById = useCallback(async (competitionId) => {
        setLoading(true);
        setError(null);
        try {
            const response = await getCompetitionById(competitionId);
            if (response === "false") {
                throw new Error('Failed to fetch competition');
            }

            const data = await response.json();
            return data;
        } catch (err) {
            setError(err.message);
            console.error('Error loading competition:', err);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    // In CompetitionContext.jsx - update the createCompetition function
    const createCompetition = useCallback(async (organiserId, data) => {
        setLoading(true);
        setError(null);
        try {

            // Check if we're updating an existing competition with boulders that have judge assignments
            const existingBoulders = data._id ? data.boulderNames : [];

            // Prepare boulders array - preserve existing boulder data if editing
            const bouldersArray = data.boulderNames.map((boulder, index) => {
                // If editing and boulder exists, preserve its properties
                if (data._id && existingBoulders[index] && typeof existingBoulders[index] === 'object') {
                    return {
                        ...existingBoulders[index], // Keep all existing properties (judgeId, maxScore, timeLimit, _id)
                        name: boulder.name || boulder // Update the name
                    };
                }
                // If creating new or boulder doesn't exist yet
                return typeof boulder === 'object' ? boulder : { name: boulder };
            });

            // Prepare competition data matching React Native structure EXACTLY
            const competitionData = {
                name: data.name,
                startDate: data.startDate,
                endDate: data.endDate,
                category: data.category,
                numberofboulders: parseInt(data.numberOfBoulders, 10),
                status: data.status ? data.status : "upcoming",
                organizerId: organiserId,
                _id: data._id ? data._id : undefined,
                boulders: bouldersArray.slice(0, parseInt(data.numberOfBoulders, 10)),
                climbTime: Number(data.climbTime),
                warningTime: Number(data.warningTime),
                wrapTime: Number(data.wrapTime),
                toppoints: Number(data.toppoints),
                zonepoints: Number(data.zonepoints),
                penalty: Number(data.penalty)
            };

            console.log('Submitting competition data:', competitionData);
            console.log('Boulders array:', competitionData.boulders);

            // Send to API
            const response = await createCompetitionAPI(organiserId, competitionData);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const apiData = await response.json();
            console.log('API Response:', apiData);

            // Add the new competition to local state
            if (apiData) {
                setCompetitions(prev => [...prev, apiData]);
            }

            return apiData;
        } catch (err) {
            setError(err.message);
            console.error('Error creating competition:', err);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    // Local state updates
    // const updateCompetition = useCallback((id, updates) => {
    //     setCompetitions(prev =>
    //         prev.map(comp =>
    //             comp.id === id ? { ...comp, ...updates } : comp
    //         )
    //     );
    // }, []);
    // Create competition with API call - UPDATED to match React Native structure
    const updateCompetition = useCallback(async (id, updates) => {
        setLoading(true);
        setError(null);
        try {

            const response = await createCompetitionAPI(id, updates);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const apiData = await response.json();
            console.log('API Response:', apiData);

            // Add the new competition to local state
            if (apiData.competition) {
                setCompetitions(prev => [...prev, apiData.competition]);

            }

            return apiData.competition;
        } catch (err) {
            setError(err.message);
            console.error('Error creating competition:', err);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);


    // Clear error
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    const value = {
        competitions,
        loading,
        error,
        // API functions
        loadCompetitions,
        loadCompetitionById,
        createCompetition,
        // Local state functions
        updateCompetition,
        clearError,
    };

    return (
        <CompetitionContext.Provider value={value}>
            {children}
        </CompetitionContext.Provider>
    );
};

